import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { EnrollmentUpdateRow, UPDATE_TYPE, UpdateTray } from "../../../../models/EnrollmentUpdateDTO";
import { FiltroCasillas } from "../../../../components/horarios/buscadorHorarios";
import S from "./strings";
import { TabProps, TYPE_LABELS, dinero, errorText, fechaCorta, nombreHorario } from "./helpers";
import { PromptModal, UpdateDetail } from "./piezas";

type Tray = Extract<UpdateTray, "pending" | "returned" | "mine">;

/** Lo que pide una decisión de Caja (sesiones de más, reparto de dinero) se revisa abierto; lo demás se aplica de un clic. */
const necesitaRevision = (r: EnrollmentUpdateRow): boolean =>
	r.typeId === null || r.typeId === UPDATE_TYPE.COURSE_CHANGE || r.typeId === UPDATE_TYPE.RETIRED || (r.preview?.sessionsMore || 0) > 0 || (r.preview?.paidNotCoursed || 0) > 0;

interface Lote { key: string; rows: EnrollmentUpdateRow[]; }

const tituloDe = (l: Lote): string => {
	const r = l.rows[0];
	if (r.batchId && r.batchKind !== null) {
		const origen = nombreHorario(r.fromSchedule?.name); const destino = nombreHorario(r.toSchedule?.name);
		if (r.batchLabel && !/^Lote de \d+/.test(r.batchLabel)) { return r.batchLabel; }
		if (origen && destino && l.rows.every((x) => x.toSchedule?.id === r.toSchedule?.id)) { return `${origen} → ${destino}`; }
		return r.batchLabel || `${origen || "—"}`;
	}
	const to = r.toCourse ? ` → ${r.toCourse.name}${r.toSchedule ? ", " + nombreHorario(r.toSchedule.name) : ""}` : r.toSchedule ? ` → ${nombreHorario(r.toSchedule.name)}` : "";
	return `${TYPE_LABELS[r.typeId || 0] || r.typeLabel || S.choose}, ${r.studentName}${r.typeId === UPDATE_TYPE.COURSE_CHANGE ? "" : to}`;
};

const PorAplicarTab: React.FC<TabProps & { initialId?: number }> = ({ campusId, countryId, movil, canApplyNow, notify, changed, initialId }) => {
	const http = HttpManager.getInstance();
	const [tray, setTray] = useState<Tray>("pending");
	const [rows, setRows] = useState<EnrollmentUpdateRow[] | null>(null);
	const [counts, setCounts] = useState<{ pending: number; returned: number }>({ pending: 0, returned: 0 });
	const [open, setOpen] = useState<number | null>(initialId ?? null);
	const [perm, setPerm] = useState<{ isCaja: boolean; isAcademic: boolean }>({ isCaja: false, isAcademic: false });
	const [rejecting, setRejecting] = useState<EnrollmentUpdateRow | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [tipo, setTipo] = useState<string[]>([]);
	const [pidio, setPidio] = useState<string[]>([]);
	const [horario, setHorario] = useState<string[]>([]);
	const [tick, setTick] = useState<number>(0);

	useEffect(() => {
		let vigente = true;
		setRows(null);
		http.enrollmentUpdatesService.list(campusId, tray, { perPage: 200 }).then((r) => {
			if (!vigente) { return; }
			// Lotes juntos, en el orden en que llegaron.
			setRows([...r.data.data].sort((a, b) => (a.batchId || 0) - (b.batchId || 0) || a.id - b.id));
			if (r.data.counts) { setCounts({ pending: r.data.counts.pending, returned: r.data.counts.returned }); }
			const primera = r.data.data[0];
			if (primera) { http.enrollmentUpdatesService.options(campusId, primera.enrollmentId).then((o) => vigente && setPerm({ isCaja: o.data.isCaja, isAcademic: o.data.isAcademic })).catch(() => { /* sin rol: se ofrece Revisar */ }); }
		}).catch((e) => vigente && notify(errorText(e), false));
		return () => { vigente = false; };
	}, [campusId, tray, tick]);

	const filtradas = useMemo(() => (rows || []).filter((r) => {
		if (tipo.length && !tipo.includes(String(r.typeId))) { return false; }
		if (pidio.length && !pidio.includes(String(r.requestedById))) { return false; }
		if (horario.length && !horario.includes(String(r.fromSchedule?.id)) && !horario.includes(String(r.toSchedule?.id))) { return false; }
		return true;
	}), [rows, tipo, pidio, horario]);

	const lotes: Lote[] = useMemo(() => {
		const out: Lote[] = [];
		filtradas.forEach((r) => {
			const key = r.batchId ? "b" + r.batchId : "u" + r.id;
			const ultimo = out[out.length - 1];
			if (ultimo && ultimo.key === key) { ultimo.rows.push(r); } else { out.push({ key, rows: [r] }); }
		});
		return out;
	}, [filtradas]);

	const opcionesTipo = useMemo(() => Array.from(new Set((rows || []).map((r) => r.typeId).filter((x): x is number => x !== null))).map((id) => ({ v: String(id), t: TYPE_LABELS[id] || String(id) })), [rows]);
	const opcionesPidio = useMemo(() => {
		const m = new Map<number, string>();
		(rows || []).forEach((r) => { if (r.requestedById && r.requestedBy) { m.set(r.requestedById, r.requestedBy); } });
		return Array.from(m.entries()).map(([id, name]) => ({ v: String(id), t: name }));
	}, [rows]);
	const opcionesHorario = useMemo(() => {
		const m = new Map<number, string>();
		(rows || []).forEach((r) => { [r.fromSchedule, r.toSchedule].forEach((s) => { if (s) { m.set(s.id, nombreHorario(s.name)); } }); });
		return Array.from(m.entries()).map(([id, name]) => ({ v: String(id), t: name }));
	}, [rows]);

	const recargar = (msg?: string) => { if (msg) { notify(msg); } setOpen(null); setTick((t) => t + 1); changed(); };
	const puedeAplicar = (r: EnrollmentUpdateRow) => canApplyNow && (perm.isCaja || (perm.isAcademic && !r.needsCashier));

	const aplicar = (r: EnrollmentUpdateRow) => {
		setBusy(true);
		http.enrollmentUpdatesService.apply(campusId, r.id, {}).then(() => recargar(S.applied)).catch((e) => notify(`${r.studentName}: ${errorText(e)}`, false)).finally(() => setBusy(false));
	};
	const aplicarLote = async (l: Lote) => {
		const simples = l.rows.filter((r) => r.statusId === 1 && !necesitaRevision(r) && puedeAplicar(r));
		setBusy(true);
		let hechas = 0;
		for (const r of simples) {
			try { await http.enrollmentUpdatesService.apply(campusId, r.id, {}); hechas++; }
			catch (e) { notify(`${r.studentName}: ${errorText(e)}`, false); break; }
		}
		setBusy(false);
		if (hechas) { setTick((t) => t + 1); changed(); setOpen(null); if (hechas === simples.length) { notify(S.appliedN(hechas)); } }
	};
	const rechazar = (motivo: string) => {
		if (!rejecting) { return; }
		setBusy(true);
		http.enrollmentUpdatesService.discard(campusId, rejecting.id, motivo)
			.then(() => { setRejecting(null); recargar(S.rejected); })
			.catch((e) => notify(errorText(e), false)).finally(() => setBusy(false));
	};

	const dineroPill = (r: EnrollmentUpdateRow) => {
		const p = r.preview;
		const partes: React.ReactNode[] = [];
		if (p?.overdue) { partes.push(<span key="v" className="wau-pill warn">{S.payOverdue(dinero(p.overdue, countryId))}</span>); }
		if (p?.paidNotCoursed) { partes.push(<span key="n" className="wau-pill warn">{S.paidNotCoursed} {dinero(p.paidNotCoursed, countryId)}</span>); }
		if (p?.sessionsMore) { partes.push(<span key="m" className="wau-pill warn">{S.moreSessions(p.sessionsMore)}</span>); }
		return partes.length ? <>{partes}</> : <span className="wau-pill ok">{S.col.moneyOk}</span>;
	};
	const cambio = (r: EnrollmentUpdateRow) => TYPE_LABELS[r.typeId || 0] || r.typeLabel || S.choose;
	const botones = (r: EnrollmentUpdateRow) => (
		<div className="wau-fila" style={{ gap: 6, flexWrap: "nowrap" }}>
			{tray === "pending" && puedeAplicar(r) && !necesitaRevision(r)
				? <button type="button" className="wau-btn p mini" disabled={busy} onClick={() => aplicar(r)}>{S.apply}</button>
				: <button type="button" className="wau-btn p mini" aria-expanded={open === r.id} onClick={() => setOpen(open === r.id ? null : r.id)}>{tray === "pending" && puedeAplicar(r) ? S.review : S.open}</button>}
			<button type="button" className="wau-btn s mini" disabled={busy} onClick={() => setRejecting(r)}>{S.reject}</button>
		</div>
	);
	const detalleAbierto = (r: EnrollmentUpdateRow) => open === r.id
		? <UpdateDetail campusId={campusId} countryId={countryId} id={r.id} notifyError={(m) => notify(m, false)} onChanged={(m) => recargar(m)} /> : null;

	const trays: { key: Tray; label: string; n?: number }[] = [
		{ key: "pending", label: S.trays.pending, n: counts.pending }, { key: "returned", label: S.trays.returned, n: counts.returned }, { key: "mine", label: S.trays.mine }
	];

	return (
		<div className="wau-pila">
			<div className="wau-fila" style={{ alignItems: "center" }}>
				<div className="wau-seg" role="group">
					{trays.map((t) => (
						<button key={t.key} type="button" aria-pressed={tray === t.key} onClick={() => { setTray(t.key); setOpen(null); setTipo([]); setPidio([]); setHorario([]); }}>
							{t.label}{t.n ? <span className="n">{t.n}</span> : null}
						</button>
					))}
				</div>
				<div className="wau-chips" style={{ marginLeft: movil ? 0 : "auto", marginBottom: 0 }}>
					<FiltroCasillas etiqueta={S.filterType} opciones={opcionesTipo} elegidos={tipo} onCambio={setTipo} />
					<FiltroCasillas etiqueta={S.filterRequester} opciones={opcionesPidio} elegidos={pidio} onCambio={setPidio} />
					<FiltroCasillas etiqueta={S.filterSchedule} opciones={opcionesHorario} elegidos={horario} onCambio={setHorario} />
				</div>
			</div>

			{rows === null ? <div className="wau-caja wau-sec">{S.loading}</div> : lotes.length === 0 ? <div className="wau-caja wau-sec">{S.empty}</div> : lotes.map((l) => {
				const r0 = l.rows[0];
				const simples = tray === "pending" ? l.rows.filter((r) => !necesitaRevision(r) && puedeAplicar(r)).length : 0;
				return (
					<div className="wau-caja wau-pila" key={l.key}>
						<div className="wau-fila" style={{ alignItems: "center" }}>
							<h3 style={{ margin: 0, flex: "1 1 260px", minWidth: 0 }}>{tituloDe(l)}</h3>
							<span className="wau-sec small">{r0.requestedBy || "—"}, {fechaCorta(r0.requestedAt)}</span>
							{simples > 1 ? <button type="button" className="wau-btn p mini" disabled={busy} onClick={() => aplicarLote(l)}>{S.applyN(simples)}</button> : null}
						</div>
						{movil ? l.rows.map((r) => (
							<div className="wau-tarjeta" key={"u" + r.id}>
								<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><b>{r.studentName}</b><span>{cambio(r)}</span></div>
								<div style={{ margin: "4px 0" }}>{dineroPill(r)}</div>
								{r.statusId === 2 && r.returnReason ? <div className="linea wau-oro">{S.fixReason}: {r.returnReason}</div> : null}
								<div className="linea">{r.note}</div>
								<div style={{ marginTop: 6 }}>{botones(r)}</div>
								{detalleAbierto(r)}
							</div>
						)) : (
							<div className="wau-tabla">
								<table>
									<thead><tr><th>{S.col.student}</th><th>{S.col.change}</th><th>{S.col.money}</th><th>{S.col.note}</th><th /></tr></thead>
									<tbody>
										{l.rows.map((r) => (
											<React.Fragment key={"u" + r.id}>
												<tr className={open === r.id ? "sel" : ""}>
													<td><b>{r.studentName}</b></td>
													<td>{cambio(r)}{r.toCourse ? <div className="wau-sec small">{nombreHorario(r.fromSchedule?.name) || "—"} → {r.toCourse.name}</div> : null}</td>
													<td>{dineroPill(r)}</td>
													<td className="wau-sec">{r.note}{r.statusId === 2 && r.returnReason ? <div className="wau-oro">{S.fixReason}: {r.returnReason}</div> : null}</td>
													<td className="der">{botones(r)}</td>
												</tr>
												{open === r.id ? <tr><td colSpan={5} style={{ padding: 0 }}>{detalleAbierto(r)}</td></tr> : null}
											</React.Fragment>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				);
			})}

			{rejecting ? <PromptModal title={S.reject} label={S.rejectReason} confirm={S.reject} busy={busy} onCancel={() => setRejecting(null)} onConfirm={rechazar}>
				<p className="wau-sec" style={{ marginTop: 0 }}>{rejecting.studentName}, {cambio(rejecting)}</p>
			</PromptModal> : null}
		</div>
	);
};

export default PorAplicarTab;
